import { useState } from "react";

import { toBrowserLocalDay, toISODateTime } from "common/utils/dates";
import { UserSubscriptionAction } from "common/types";

import { useUpdateSubscriptionMutation } from "client/subscriptions/hooks";
import { useFeatures } from "client/clinics/hooks";
import { minSelectableDeliveryDate, minSelectableNextOrderDate } from "client/subscriptions/utils";
import Calendar from "client/common/components/calendars/Calendar";
import { useInvalidateOnboarding } from "client/onboarding/hooks/invalidate";
import Modal from "client/common/components/deprecated-modals/Modal";
import ModalLayout from "client/common/components/deprecated-modals/ModalLayout";
import SubscriptionDateBanners from "client/subscriptions/components/SubscriptionDateBanners";

type UpdateNextOrderDateModalProps = {
  isVisible: boolean;
  onClose: () => void;
  subscriptionId: number;
  initialNextOrderDate: string | null;
  initialExpectedDeliveryBy: string | null;
};

const UpdateNextOrderDateModal = ({
  isVisible,
  onClose,
  subscriptionId,
  initialNextOrderDate,
  initialExpectedDeliveryBy,
}: UpdateNextOrderDateModalProps) => {
  const isEdbEnabled = useFeatures().AUTOSHIP_EDB;
  const invalidateOnboarding = useInvalidateOnboarding();
  const {
    mutate: updateSubscription,
    isPending: isUpdating,
    error: updateError,
  } = useUpdateSubscriptionMutation(subscriptionId, {
    onSuccess: () => {
      invalidateOnboarding();
      onClose();
    },
  });
  const [scheduleDate, setScheduleDate] = useState(() => {
    const initial = isEdbEnabled ? initialExpectedDeliveryBy : initialNextOrderDate;
    const dt = initial ? new Date(initial) : new Date();
    // v2 delivery dates cannot fall inside the min lead-time window; start on the
    // earliest selectable date so the picker never opens on a disabled day.
    if (isEdbEnabled && dt < minSelectableDeliveryDate()) {
      return minSelectableDeliveryDate();
    }
    return dt;
  });

  const onSubmit = () => {
    updateSubscription({
      action: UserSubscriptionAction.Modify,
      ...(isEdbEnabled
        ? { expected_delivery_by: toISODateTime(scheduleDate) }
        : { next_order_date: toISODateTime(scheduleDate) }),
    });
  };

  return (
    <Modal visible={isVisible} onClose={onClose}>
      <ModalLayout>
        <ModalLayout.Header
          title={isEdbEnabled ? "Update next delivery" : "Update next order date"}
          onClose={onClose}
        />
        <ModalLayout.Form
          isLoading={isUpdating}
          canSubmit={true}
          submitText={isEdbEnabled ? "Update delivery date" : "Update order date"}
          onSubmit={onSubmit}
          size="l"
          error={updateError}
          fieldErrors={[
            isEdbEnabled
              ? updateError?.errors.expected_delivery_by
              : updateError?.errors.next_order_date,
          ]}
        >
          <Calendar
            disabledDays={{
              before: toBrowserLocalDay(
                isEdbEnabled ? minSelectableDeliveryDate() : minSelectableNextOrderDate(),
              ),
            }}
            selectedDays={toBrowserLocalDay(scheduleDate)}
            onSelect={setScheduleDate}
          />
          <SubscriptionDateBanners dates={[scheduleDate]} />
        </ModalLayout.Form>
      </ModalLayout>
    </Modal>
  );
};

export default UpdateNextOrderDateModal;
